<script setup lang="ts">
import { ref, onUnmounted, onBeforeMount, computed } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime } from "luxon";

import useVideoCallStore from "../../Store";
import useUserStore from "@/Modules/User/Store";

import InfoCircleIcon from "@/Components/Icons/InfoCircle.vue";
import DocumentIcon from "@/Components/Icons/Document.vue";
import ClockIcon from "@/Components/Icons/Clock.vue";

const { t } = useI18n();
const videoCallStore = useVideoCallStore();
const userStore = useUserStore();

const emit = defineEmits<{
  (event: "clickTranscription"): void;
}>();

const intervalIndex = ref<number>();

const remainingSeconds = computed(() => {
  const { startDate, currentDate } = videoCallStore.timeCounter;

  const elapsedSeconds = DateTime.fromJSDate(currentDate)
    .diff(DateTime.fromJSDate(startDate), "seconds")
    .as("seconds");

  const totalSessionSeconds = videoCallStore.sessionDuration * 60;

  return Math.max(0, Math.floor(totalSessionSeconds - elapsedSeconds));
});

const isEndedTime = computed(() => remainingSeconds.value <= 0);
const remainTime = computed(() => {
  if (isEndedTime.value) {
    return "00:00";
  }

  const minutes = Math.floor(remainingSeconds.value / 60);
  const seconds = remainingSeconds.value % 60;

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
});

const handleClickInfoTranscription = () => {
  if (userStore.getUserIsPatient) {
    emit("clickTranscription");
  }
};

onBeforeMount(() => {
  videoCallStore.updateStartDate(new Date());

  intervalIndex.value = setInterval(() => {
    videoCallStore.updateCurrentDate(new Date());
  }, 1000) as unknown as number;
});

onUnmounted(() => {
  clearInterval(intervalIndex.value);
});
</script>

<template>
  <div class="counter" id="video_call_counter">
    <div
      v-if="userStore.getUserIsTherapist"
      class="counter__remainder_card"
      :class="{
        'counter__remainder_card--error': isEndedTime,
      }"
    >
      <ClockIcon class="mr-2" :color="isEndedTime ? 'semantic-error' : 'content-high'" />

      {{ t("general.chat.videocall.reminder_card", { time: remainTime }) }}
    </div>

    <div
      v-if="videoCallStore.isTranscriptionEnabled"
      class="counter__transcription_card"
      :class="{ 'c-pointer': userStore.getUserIsPatient }"
      @click="handleClickInfoTranscription"
    >
      <DocumentIcon class="mr-2" color="content-high" />

      {{ t("general.chat.videocall.transiction_card") }}

      <InfoCircleIcon class="ml-2" color="content-high" />
    </div>

    <p class="text-left typography-low-xl color-content-inverse">
      {{ videoCallStore.durationStringWithSecs }}
    </p>

    <p class="text-left typography-low-m color-content-inverse">
      {{ t("general.chat.videocall.time_title") }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.counter {
  &__transcription_card,
  &__remainder_card {
    @include sp.padding(y, 1);
    @include sp.padding(x, 2);
    @include sp.margin(b, 2);
    @include text.general(low, s);
    @include colors.color(content, high);
    @include radius(s);

    display: flex;
    align-items: center;
    font-weight: 700;

    &.c-pointer {
      &:hover {
        @include elevation(m);

        background-color: rgba(217, 217, 217, 1);
      }
    }
  }

  &__transcription_card {
    background-color: rgba(217, 217, 217, 0.55);
  }

  &__remainder_card {
    @include colors.bg(semantic, success-low);

    &-error {
      @include colors.bg(semantic, error-low);
    }
  }
}
</style>
